import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT, Environment, ValidationError } from '@netfoundry-ui/shared/model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { likeContains } from './frontdoor-v3-execution.service';
import { checkFrontdoorBody } from './frontdoor-v3-validation';
import { LoggerService } from './logger.service';
import { HTTP_CLIENT } from './token.service';

/** How far the verification of a domain name's CNAME has got. */
export type DomainNameStatus = 'PENDING' | 'VERIFIED' | 'FAILED';

export const DOMAIN_NAME_STATUSES: DomainNameStatus[] = ['PENDING', 'VERIFIED', 'FAILED'];

/** A customer-owned domain name in a v3 frontdoor. The customer points the wildcard `recordName` at `target` with a CNAME. */
export interface DomainNameV3 {
    [key: string]: any;

    id: string;
    frontdoorId: string;
    /** The fully qualified name, in lowercase and without a trailing dot. */
    name: string;
    description?: string;
    /** The name of the CNAME record the customer creates: `*.<name>`. Each share answers at `<share-name>.<name>`. */
    recordName: string;
    /** The NetFoundry-owned name that the CNAME record must point at. */
    target: string;
    status: DomainNameStatus;
    /** Why the verification stopped. Set only when the status is `FAILED`. */
    failureReason?: string;
    verifiedAt?: string;
    /** The live shares under this name that the caller can read. A delete counts every share, so 0 does not promise that a delete succeeds. */
    shareCount: number;
    /** Whether this is the account's default name. NetFoundry creates it and owns its DNS record, so it cannot be deleted. */
    isDefault?: boolean;
    createdBy?: string;
    createdAt?: string;
    updatedBy?: string;
    updatedAt?: string;
}

/** A request to add a domain name. The name cannot change after the create. */
export interface CreateDomainNameV3 {
    name: string;
    description?: string;
}

/** A request to change a domain name. Only `description` can change, and `null` clears it. */
export interface UpdateDomainNameV3 {
    description?: string | null;
}

/** One page of domain names, with the total across all pages. */
export interface DomainNamePageV3 {
    domainNames: DomainNameV3[];
    total: number;
}

/** What a domain name listing can be narrowed by at the service. */
export interface DomainNameListFilters {
    /** One value, which the service reads as a case-insensitive contains. */
    name?: string;
    statuses?: DomainNameStatus[];
}

export const DEFAULT_DOMAIN_NAME_PAGE_SIZE = 50;

/** The query parameter on the Domain Names page that opens the detail drawer on one id. */
export const FD_DOMAIN_NAME_QUERY_PARAM = 'domainName';

/** Why the default domain name cannot be deleted. The list, the drawer and the delete guard show it. */
export const DEFAULT_DOMAIN_NAME_DELETE_HINT = 'The default domain name cannot be deleted. NetFoundry owns its DNS record.';

/** The longest description the service accepts. */
export const MAX_DOMAIN_NAME_DESCRIPTION_LENGTH = 1024;

/** How a status reads on screen. `hint` goes in a tooltip, and is '' where the label says it all. */
export interface DomainNameStatusBadge {
    tone: 'ok' | 'warn' | 'err' | 'muted';
    label: string;
    hint: string;
}

const DOMAIN_NAME_STATUS_BADGES: Record<DomainNameStatus, DomainNameStatusBadge> = {
    PENDING: { tone: 'warn', label: 'Pending', hint: 'Waiting for the CNAME record' },
    VERIFIED: { tone: 'ok', label: 'Verified', hint: '' },
    FAILED: { tone: 'err', label: 'Failed', hint: '' },
};

/** The badge for `domainName`. A failed name carries its failure reason as the hint. */
export function domainNameStatusBadge(domainName: DomainNameV3 | undefined): DomainNameStatusBadge {
    const badge = DOMAIN_NAME_STATUS_BADGES[domainName?.status as DomainNameStatus];
    if (!badge) {
        return { tone: 'muted', label: 'Unknown', hint: '' };
    }
    return domainName?.status === 'FAILED' ? { ...badge, hint: domainName.failureReason ?? '' } : badge;
}

/** Adds the filters a listing asks for. An absent or empty filter is left off. */
export function withDomainNameFilters(params: HttpParams, filters: DomainNameListFilters = {}): HttpParams {
    const name = (filters.name ?? '').trim();
    let withFilters = name.length > 0 ? params.set('name', likeContains(name)) : params;
    for (const status of filters.statuses ?? []) {
        if (status) {
            withFilters = withFilters.append('status', status);
        }
    }
    return withFilters;
}

/** Reads and writes the domain names of one v3 frontdoor. Every path is scoped by the frontdoor id. */
@Injectable({ providedIn: 'root' })
export class FrontdoorV3DomainNameService {
    private readonly apiUrl: string;

    constructor(
        private logger: LoggerService,
        @Inject(HTTP_CLIENT) private http: HttpClient,
        @Inject(ENVIRONMENT) private environment: Environment
    ) {
        this.apiUrl = this.environment.frontdoorURL;
    }

    /** One page of `frontdoorId`'s domain names. The service filters the rows to what the caller can read. */
    public findDomainNamePage(
        frontdoorId: string,
        sort = 'createdAt,DESC',
        size = DEFAULT_DOMAIN_NAME_PAGE_SIZE,
        filters: DomainNameListFilters = {},
        page = 0
    ): Observable<DomainNamePageV3> {
        const params = withDomainNameFilters(
            new HttpParams().set('sort', sort).set('size', String(size)).set('page', String(page)),
            filters
        );
        return this.http
            .get(this.domainNamesUrl(frontdoorId), { headers: this.headers(), params })
            .pipe(map((response: any) => this.readPage(response)));
    }

    public getDomainName(frontdoorId: string, id: string): Observable<DomainNameV3> {
        return this.http
            .get(this.domainNameUrl(frontdoorId, id), { headers: this.headers() })
            .pipe(map((response: any) => response as DomainNameV3));
    }

    /** Adds a domain name and starts its verification. The response carries the `target` for the CNAME. */
    public createDomainName(frontdoorId: string, create: CreateDomainNameV3): Observable<DomainNameV3> {
        return this.http
            .post(this.domainNamesUrl(frontdoorId), { ...create, frontdoorId }, { headers: this.headers() })
            .pipe(map((response: any) => response as DomainNameV3));
    }

    /** Whether {@link createDomainName} would be accepted. Sends the same body with the `nf-validate` header. */
    public validateCreateDomainName(frontdoorId: string, create: CreateDomainNameV3): Observable<ValidationError[]> {
        return checkFrontdoorBody(this.http, this.domainNamesUrl(frontdoorId), { ...create, frontdoorId });
    }

    /** Applies `update` with a `PATCH`, so only the fields in the body change. */
    public updateDomainName(frontdoorId: string, id: string, update: UpdateDomainNameV3): Observable<DomainNameV3> {
        return this.http
            .patch(this.domainNameUrl(frontdoorId, id), update, { headers: this.headers() })
            .pipe(map((response: any) => response as DomainNameV3));
    }

    /** Whether {@link updateDomainName} would be accepted. Uses `PATCH`, the same method as the write. */
    public validateUpdateDomainName(frontdoorId: string, id: string, update: UpdateDomainNameV3): Observable<ValidationError[]> {
        return checkFrontdoorBody(this.http, this.domainNameUrl(frontdoorId, id), update, 'PATCH');
    }

    /** Checks the CNAME now. A `FAILED` name goes back to `PENDING` and its 72-hour window starts again. */
    public verifyDomainName(frontdoorId: string, id: string): Observable<DomainNameV3> {
        return this.http
            .post(`${this.domainNameUrl(frontdoorId, id)}/verify`, {}, { headers: this.headers() })
            .pipe(map((response: any) => response as DomainNameV3));
    }

    /** Deletes the domain name. The service answers 409 while any share uses it, also a share the caller cannot read. */
    public deleteDomainName(frontdoorId: string, id: string): Observable<any> {
        return this.http.delete(this.domainNameUrl(frontdoorId, id), { headers: this.headers() });
    }

    private domainNamesUrl(frontdoorId: string): string {
        return `${this.apiUrl}/v3/${frontdoorId}/domain-names`;
    }

    private domainNameUrl(frontdoorId: string, id: string): string {
        return `${this.domainNamesUrl(frontdoorId)}/${id}`;
    }

    private headers(): HttpHeaders {
        return new HttpHeaders().set('Accept', 'application/hal+json');
    }

    private readPage(response: any): DomainNamePageV3 {
        const embedded = response?._embedded;
        const domainNames = embedded?.domainNames ?? (embedded ? Object.values(embedded)[0] : undefined);
        if (embedded && !Array.isArray(domainNames)) {
            this.logger.error('findDomainNamePage: unrecognised response shape', response);
        }
        const rows = Array.isArray(domainNames) ? (domainNames as DomainNameV3[]) : [];
        const total = Number.isFinite(response?.page?.totalElements) ? response.page.totalElements : rows.length;
        return { domainNames: rows, total };
    }
}
